import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  TextField,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CancelIcon from "@mui/icons-material/Cancel";
import { ExportDataButton } from "./components/ExportDataDialog";
import {
  getDefaultExportDateFrom,
  getDefaultExportDateTo,
  isDateInRange,
} from "./utils/exportRetailData";
import { obtenerFechaHoyISO } from "../../utils/horaLocal";

export default function HistorialVentas({ store, permissions }) {
  const [expandida, setExpandida] = useState(null);
  const [fechaDesde, setFechaDesde] = useState(() => getDefaultExportDateFrom());
  const [fechaHasta, setFechaHasta] = useState(() => getDefaultExportDateTo());
  const [feedback, setFeedback] = useState(null);

  const canAnularVenta = permissions?.actions?.ANULAR_VENTA !== false;

  const formatMoney = (value) =>
    `${store.config.moneda} ${(parseFloat(value) || 0).toFixed(2)}`;

  const ventasOrdenadas = useMemo(() => {
    const ventasMap = new Map((store.ventas || []).map((v) => [v.id, { ...v, _inferred: false }]));

    (store.detalleVentas || []).forEach((d) => {
      const ventaId = String(d.venta_id || "").trim();
      if (!ventaId || ventasMap.has(ventaId)) return;

      ventasMap.set(ventaId, {
        id: ventaId,
        fecha: null,
        metodo_pago: "N/D",
        subtotal: 0,
        descuento_total: 0,
        total: 0,
        anulado: false,
        descuentos: [],
        _inferred: true,
      });
    });

    for (const venta of ventasMap.values()) {
      if (!venta._inferred) continue;
      const detalles = (store.detalleVentas || []).filter((d) => d.venta_id === venta.id);
      const subtotal = detalles.reduce(
        (acc, d) => acc + (parseInt(d.cantidad, 10) || 0) * (parseFloat(d.precio_unitario) || 0),
        0
      );
      venta.subtotal = subtotal;
      venta.total = subtotal;
    }

    let ventas = [...ventasMap.values()].sort((a, b) => {
      const ta = a?.fecha ? new Date(a.fecha).getTime() : 0;
      const tb = b?.fecha ? new Date(b.fecha).getTime() : 0;
      return tb - ta;
    });

    if (fechaDesde || fechaHasta) {
      ventas = ventas.filter((v) => isDateInRange(v.fecha, fechaDesde, fechaHasta));
    }

    return ventas;
  }, [store.detalleVentas, store.ventas, fechaDesde, fechaHasta]);

  const handleAnular = (ventaId) => {
    if (!canAnularVenta) {
      setFeedback({ tipo: "warning", msg: "No tienes permisos para anular ventas" });
      return;
    }

    if (window.confirm("¿Anular esta venta? El stock se restaurará.")) {
      store.anularVenta(ventaId);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Typography variant="h6" fontWeight={700}>
          Historial de Ventas ({ventasOrdenadas.length})
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <ExportDataButton
            dataset="ventas"
            store={store}
            ventas={ventasOrdenadas}
            label="Exportar"
          />
          <TextField
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            size="small"
            sx={{ maxWidth: 160 }}
            InputLabelProps={{ shrink: true }}
            label="Desde"
          />
          <TextField
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            size="small"
            sx={{ maxWidth: 160 }}
            InputLabelProps={{ shrink: true }}
            label="Hasta"
          />
        </Stack>
      </Stack>

      {feedback && (
        <Alert severity={feedback.tipo} sx={{ mb: 2 }}>
          {feedback.msg}
        </Alert>
      )}

      {ventasOrdenadas.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
          No hay ventas en el rango seleccionado
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                <TableCell width={40}></TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Método</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell align="right">Descuento</TableCell>
                <TableCell align="right">Total Neto</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ventasOrdenadas.map((v) => {
                const detalles = store.detalleVentas.filter(
                  (d) => d.venta_id === v.id
                );
                const isExpanded = expandida === v.id;
                const subtotalVenta = parseFloat(v.subtotal) || 0;
                const descuentoVenta = Math.max(0, parseFloat(v.descuento_total) || 0);
                const totalNeto = Math.max(0, parseFloat(v.total) || subtotalVenta - descuentoVenta);

                const subtotalDetalle = detalles.reduce(
                  (acc, d) => acc + (parseInt(d.cantidad) || 0) * (parseFloat(d.precio_unitario) || 0),
                  0
                );

                return [
                  <TableRow
                    key={v.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      opacity: v.anulado ? 0.5 : 1,
                    }}
                    onClick={() =>
                      setExpandida(isExpanded ? null : v.id)
                    }
                  >
                      <TableCell>
                        {isExpanded ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {v.fecha
                          ? new Date(v.fecha).toLocaleDateString("es", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "Sin fecha"}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <Chip
                            label={v.metodo_pago}
                            size="small"
                            variant="outlined"
                          />
                          {v.metodo_pago === "MIXTO" && v.metodo_pago_detalle && (
                            <Typography variant="caption" color="text.secondary">
                              Efe: {store.config.moneda} {(parseFloat(v.metodo_pago_detalle.efectivo) || 0).toFixed(2)} · QR: {store.config.moneda} {(parseFloat(v.metodo_pago_detalle.qr) || 0).toFixed(2)}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatMoney(subtotalVenta)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={descuentoVenta > 0 ? "warning.main" : "text.secondary"}
                        >
                          -{formatMoney(descuentoVenta)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatMoney(totalNeto)}
                      </TableCell>
                      <TableCell align="center">
                        {v.anulado ? (
                          <Chip
                            label="ANULADO"
                            size="small"
                            color="error"
                          />
                        ) : (
                          <Chip
                            label="OK"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {!v.anulado && !v._inferred && canAnularVenta && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAnular(v.id);
                            }}
                            title="Anular venta"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>,
                    isExpanded ? (
                      <TableRow key={`${v.id}-detail`}>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse in={isExpanded}>
                            <Box sx={{ p: 2, bgcolor: "grey.50" }}>
                              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={1.5}>
                                <Chip size="small" color="default" variant="outlined" label={`Subtotal: ${formatMoney(subtotalVenta || subtotalDetalle)}`} />
                                <Chip size="small" color={descuentoVenta > 0 ? "warning" : "default"} variant="outlined" label={`Descuento: -${formatMoney(descuentoVenta)}`} />
                                <Chip size="small" color="success" variant="outlined" label={`Total neto: ${formatMoney(totalNeto)}`} />
                              </Stack>

                              {(Array.isArray(v.descuentos) && v.descuentos.length > 0) && (
                                <Box mb={1.5}>
                                  <Typography variant="subtitle2" fontWeight={700} mb={0.75}>
                                    Descuentos aplicados
                                  </Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {v.descuentos.map((desc, idx) => {
                                      const nombre = desc?.nombre || desc?.tipo || `Descuento ${idx + 1}`;
                                      const monto = Math.max(0, parseFloat(desc?.monto) || 0);
                                      return (
                                        <Chip
                                          key={`${v.id}-descuento-${idx}`}
                                          size="small"
                                          color="warning"
                                          variant="outlined"
                                          label={`${nombre}: -${formatMoney(monto)}`}
                                        />
                                      );
                                    })}
                                  </Stack>
                                </Box>
                              )}

                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                mb={1}
                              >
                                Detalle de la venta
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Producto</TableCell>
                                    <TableCell align="center">
                                      Cantidad
                                    </TableCell>
                                    <TableCell align="right">
                                      P. Unitario
                                    </TableCell>
                                    <TableCell align="right">
                                      Subtotal
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {detalles.map((d) => (
                                    (() => {
                                      const qty = parseInt(d.cantidad) || 0;
                                      const precio = parseFloat(d.precio_unitario) || 0;
                                      const bruto = qty * precio;
                                      const baseProrrateo = subtotalVenta > 0 ? subtotalVenta : subtotalDetalle;
                                      const proporcion = baseProrrateo > 0 ? bruto / baseProrrateo : 0;
                                      const descuentoLinea = descuentoVenta * proporcion;
                                      const netoLinea = Math.max(0, bruto - descuentoLinea);

                                      return (
                                        <TableRow key={d.id}>
                                          <TableCell>{d.nombre}</TableCell>
                                          <TableCell align="center">
                                            {qty}
                                          </TableCell>
                                          <TableCell align="right">
                                            {precio.toFixed(2)}
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{ fontWeight: 600 }}
                                          >
                                            <Typography variant="body2" fontWeight={700}>
                                              {bruto.toFixed(2)}
                                            </Typography>
                                            {descuentoVenta > 0 && (
                                              <Typography variant="caption" color="text.secondary">
                                                Neto: {netoLinea.toFixed(2)}
                                              </Typography>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })()
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    ) : null
                ];
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
